import React, { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react'

interface UseDialogOptions<T extends React.ElementType> {
  onOpen?: () => void
  onAction?: ComponentProps<T>['onAction']
  onError?: ComponentProps<T>['onError']
  onClose?: ComponentProps<T>['onClose']
}

interface UseDialogReturn<T extends React.ElementType> {
  isOpen: boolean
  handleOpen: () => void
  handleAction: NonNullable<ComponentProps<T>['onAction']>
  handleError: NonNullable<ComponentProps<T>['onError']>
  handleClose: NonNullable<ComponentProps<T>['onClose']>
}

const useDialog = <T extends React.ElementType>(options?: UseDialogOptions<T>): UseDialogReturn<T> => {
  const onActionRef = useRef(options?.onAction)
  const onErrorRef = useRef(options?.onError)
  const onCloseRef = useRef(options?.onClose)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    onActionRef.current = options?.onAction
    onCloseRef.current = options?.onClose
  }, [options])

  const handleOpen = useCallback(() => {
    options?.onOpen?.()
    setOpen(true)
  }, [])

  const handleAction = useCallback((...args: Parameters<NonNullable<ComponentProps<T>['onAction']>>) => {
    setOpen(false)
    onActionRef.current?.(...args)
  }, [])

  const handleError = useCallback((...args: Parameters<NonNullable<ComponentProps<T>['onError']>>) => {
    onErrorRef.current?.(...args)
  }, [])

  const handleClose = useCallback((...args: Parameters<NonNullable<ComponentProps<T>['onClose']>>) => {
    setOpen(false)
    onCloseRef.current?.(...args)
  }, [])

  return {
    isOpen: open,
    handleOpen,
    handleAction,
    handleError,
    handleClose,
  }
}

export default useDialog
